/**
 * PlansManagementV2 - Complete Plans & Features Management
 * 
 * Features:
 * - Industry selection with plan management
 * - Plan CRUD (Create, Read, Update, Delete)
 * - Feature Catalog CRUD with types (toggle, limit, workflow)
 * - Add-ons CRUD
 * - Real data from PostgreSQL via APIs
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import ConfirmModal from '../../components/modals/ConfirmModal';
import { 
  Building2, Package, Users, CreditCard, Check, X, Plus, Edit2, Trash2,
  ChevronDown, ChevronRight, Settings, ToggleLeft, Hash, GitBranch, Save,
  DollarSign, Zap, Shield, Eye, EyeOff, Loader2
} from 'lucide-react';

const PlansManagementV2 = () => {
  const { user } = useAuth();
  
  // Main states
  const [industries, setIndustries] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState([]);
  const [addons, setAddons] = useState([]);
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('plans'); // plans | features | addons
  const [confirmModal, setConfirmModal] = useState(null);
  
  // Modal states
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showFeatureModal, setShowFeatureModal] = useState(false);
  const [showAddonModal, setShowAddonModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Entitlements editor
  const [showEntitlementsModal, setShowEntitlementsModal] = useState(false);
  const [editingPlanForEntitlements, setEditingPlanForEntitlements] = useState(null);
  const [entitlementsForm, setEntitlementsForm] = useState({ modules: {}, limits: {} });
  const [applyingChanges, setApplyingChanges] = useState(false);

  // Form data
  const [planForm, setPlanForm] = useState({
    name: '', code: '', description: '',
    price_monthly: 0, price_yearly: 0,
    included_staff: 3, included_tracking_staff: 0,
    field_tracking_enabled: false
  });
  
  const [featureForm, setFeatureForm] = useState({
    key: '', name: '', description: '', category: 'SALES',
    is_limitable: false, default_enabled: true, default_limit_value: null
  });
  
  const [addonForm, setAddonForm] = useState({
    key: '', name: '', description: '',
    price_monthly: 0, price_yearly: 0,
    limit_increase_value: 1, limit_type: 'staff_seats'
  });
  
  // Fetch industries
  const fetchIndustries = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/plans-v2/industries');
      setIndustries(data.industries || []);
      if (data.industries?.length > 0 && !selectedIndustry) {
        setSelectedIndustry(data.industries[0]);
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load industries'));
    }
  }, [selectedIndustry]);
  
  // Fetch plans for industry
  const fetchPlans = useCallback(async () => {
    if (!selectedIndustry) return;
    try {
      const { data } = await api.get('/admin/plans-v2/plans', {
        params: { industry_id: selectedIndustry.id, include_inactive: true }
      });
      setPlans(data.plans || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load plans'));
    }
  }, [selectedIndustry]);
  
  // Fetch features
  const fetchFeatures = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/plans-v2/features');
      setFeatures(data.features || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load features'));
    }
  }, []);
  
  // Fetch add-ons
  const fetchAddons = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/plans-v2/addons');
      setAddons(data.addons || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load add-ons'));
    }
  }, []);
  
  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchIndustries();
      await fetchFeatures();
      await fetchAddons();
      setLoading(false);
    };
    loadData();
  }, [fetchIndustries, fetchFeatures, fetchAddons]);
  
  // Load plans when industry changes
  useEffect(() => {
    if (selectedIndustry) {
      fetchPlans();
    }
  }, [selectedIndustry, fetchPlans]);
  
  // ================== PLAN CRUD ==================
  const openPlanModal = (plan = null) => {
    if (plan) {
      setEditingItem(plan);
      setPlanForm({
        name: plan.name || '',
        code: plan.code || '',
        description: plan.description || '',
        price_monthly: plan.price_monthly || 0,
        price_yearly: plan.price_yearly || 0,
        included_staff: plan.included_staff || 3,
        included_tracking_staff: plan.included_tracking_staff || 0,
        field_tracking_enabled: plan.field_tracking_enabled || false
      });
    } else {
      setEditingItem(null);
      setPlanForm({
        name: '', code: '', description: '',
        price_monthly: 0, price_yearly: 0,
        included_staff: 3, included_tracking_staff: 0,
        field_tracking_enabled: false
      });
    }
    setShowPlanModal(true);
  };
  
  const savePlan = async () => {
    if (!planForm.name) {
      toast.error('Plan name is required');
      return;
    }

    setSaving(true);
    try {
      const body = editingItem
        ? planForm
        : { ...planForm, industry_id: selectedIndustry.id };

      if (editingItem) {
        await api.put(`/admin/plans-v2/plans/${editingItem.id}`, body);
      } else {
        await api.post('/admin/plans-v2/plans', body);
      }
      toast.success(editingItem ? 'Plan updated successfully' : 'Plan created successfully');
      setShowPlanModal(false);
      fetchPlans();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error saving plan'));
    } finally {
      setSaving(false);
    }
  };

  const deletePlan = (plan) => {
    setConfirmModal({
      title: 'Delete Plan',
      description: `"${plan.name}" will be marked as inactive. Existing subscribers are unaffected.`,
      confirmLabel: 'Delete Plan',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      icon: Trash2,
      iconBgClass: 'bg-red-100',
      iconClass: 'text-red-600',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await api.delete(`/admin/plans-v2/plans/${plan.id}`);
          toast.success('Plan deleted successfully');
          fetchPlans();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Error deleting plan'));
        }
      },
    });
  };

  // ================== ENTITLEMENTS EDITOR ==================
  const openEntitlementsModal = async (plan) => {
    setEditingPlanForEntitlements(plan);
    try {
      const { data } = await api.get(`/admin/plans-v2/plans/${plan.id}`);
      const ent = data.entitlements || {};
      setEntitlementsForm({
        modules: ent.modules || {},
        limits: ent.limits || {}
      });
    } catch {
      setEntitlementsForm({ modules: {}, limits: {} });
    }
    setShowEntitlementsModal(true);
  };

  const saveEntitlements = async () => {
    if (!editingPlanForEntitlements) return;
    setApplyingChanges(true);
    try {
      // Step 1: Save entitlements JSON to plan
      await api.put(
        `/admin/plans-v2/plans/${editingPlanForEntitlements.id}/entitlements`,
        {
          modules: entitlementsForm.modules,
          submodules: {},
          limits: Object.fromEntries(
            Object.entries(entitlementsForm.limits).map(([k, v]) => [k, v === '' ? null : Number(v)])
          )
        }
      );

      // Step 2: Propagate to all businesses on this plan
      let count = 0;
      try {
        const { data: applyData } = await api.post(
          `/admin/plans/${editingPlanForEntitlements.id}/apply-changes`
        );
        count = applyData?.businesses_updated ?? 0;
      } catch {
        // non-critical
      }

      toast.success(`Entitlements saved and applied to ${count} business${count !== 1 ? 'es' : ''}`);
      setShowEntitlementsModal(false);
      fetchPlans();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error saving entitlements'));
    } finally {
      setApplyingChanges(false);
    }
  };

  // ================== FEATURE CRUD ==================
  const openFeatureModal = (feature = null) => {
    if (feature) {
      setEditingItem(feature);
      setFeatureForm({
        key: feature.key || '',
        name: feature.name || '',
        description: feature.description || '',
        category: feature.category || 'SALES',
        is_limitable: feature.is_limitable || false,
        default_enabled: feature.default_enabled ?? true,
        default_limit_value: feature.default_limit_value || null
      });
    } else {
      setEditingItem(null);
      setFeatureForm({
        key: '', name: '', description: '', category: 'SALES',
        is_limitable: false, default_enabled: true, default_limit_value: null
      });
    }
    setShowFeatureModal(true);
  };

  const saveFeature = async () => {
    if (!featureForm.key || !featureForm.name) {
      toast.error('Feature key and name are required');
      return;
    }

    setSaving(true);
    try {
      if (editingItem) {
        await api.put(`/admin/plans-v2/features/${editingItem.id}`, featureForm);
      } else {
        await api.post('/admin/plans-v2/features', featureForm);
      }
      toast.success(editingItem ? 'Feature updated successfully' : 'Feature created successfully');
      setShowFeatureModal(false);
      fetchFeatures();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error saving feature'));
    } finally {
      setSaving(false);
    }
  };

  const deleteFeature = (feature) => {
    setConfirmModal({
      title: 'Delete Feature',
      description: `"${feature.name}" will be removed from the feature catalog.`,
      confirmLabel: 'Delete Feature',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      icon: Trash2,
      iconBgClass: 'bg-red-100',
      iconClass: 'text-red-600',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await api.delete(`/admin/plans-v2/features/${feature.id}`);
          toast.success('Feature deleted successfully');
          fetchFeatures();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Error deleting feature'));
        }
      },
    });
  };

  // ================== ADDON CRUD ==================
  const openAddonModal = (addon = null) => {
    if (addon) {
      setEditingItem(addon);
      setAddonForm({
        key: addon.key || '',
        name: addon.name || '',
        description: addon.description || '',
        price_monthly: addon.price_monthly || 0,
        price_yearly: addon.price_yearly || 0,
        limit_increase_value: addon.limit_increase_value || 1,
        limit_type: addon.limit_type || 'staff_seats'
      });
    } else {
      setEditingItem(null);
      setAddonForm({
        key: '', name: '', description: '',
        price_monthly: 0, price_yearly: 0,
        limit_increase_value: 1, limit_type: 'staff_seats'
      });
    }
    setShowAddonModal(true);
  };

  const saveAddon = async () => {
    if (!addonForm.key || !addonForm.name) {
      toast.error('Add-on key and name are required');
      return;
    }

    setSaving(true);
    try {
      if (editingItem) {
        await api.put(`/admin/plans-v2/addons/${editingItem.id}`, addonForm);
      } else {
        await api.post('/admin/plans-v2/addons', addonForm);
      }
      toast.success(editingItem ? 'Add-on updated successfully' : 'Add-on created successfully');
      setShowAddonModal(false);
      fetchAddons();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error saving add-on'));
    } finally {
      setSaving(false);
    }
  };

  const deleteAddon = (addon) => {
    setConfirmModal({
      title: 'Delete Add-on',
      description: `"${addon.name}" will be marked as inactive.`,
      confirmLabel: 'Delete Add-on',
      confirmClass: 'bg-red-600 hover:bg-red-700',
      icon: Trash2,
      iconBgClass: 'bg-red-100',
      iconClass: 'text-red-600',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await api.delete(`/admin/plans-v2/addons/${addon.id}`);
          toast.success('Add-on deleted successfully');
          fetchAddons();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Error deleting add-on'));
        }
      },
    });
  };
  
  // Get feature type icon
  const getFeatureTypeIcon = (type) => {
    switch (type) {
      case 'toggle': return <ToggleLeft className="w-4 h-4 text-blue-500" />;
      case 'limit': return <Hash className="w-4 h-4 text-amber-500" />;
      case 'workflow': return <GitBranch className="w-4 h-4 text-purple-500" />;
      default: return <Settings className="w-4 h-4 text-slate-400" />;
    }
  };
  
  // Group features by category
  const featuresByCategory = features.reduce((acc, f) => {
    const cat = f.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(f);
    return acc;
  }, {});
  
  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-rose-500" />
      </div>
    );
  }
  
  return (
    <div className="h-full flex flex-col bg-slate-50" data-testid="plans-management-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-[20px] font-bold text-slate-800">Plans & Features Management</h1>
            <p className="text-[13px] text-slate-500 mt-0.5">Manage plans, features, and add-ons for all industries</p>
          </div>
          
          {/* Industry Selector */}
          <div className="flex items-center gap-4">
            <select
              value={selectedIndustry?.id || ''}
              onChange={(e) => {
                const ind = industries.find(i => i.id === e.target.value);
                setSelectedIndustry(ind);
              }}
              className="px-4 py-2 border border-slate-200 rounded-lg text-[13px] font-medium text-slate-700 bg-white"
            >
              {industries.map(ind => (
                <option key={ind.id} value={ind.id}>{ind.name}</option>
              ))}
            </select>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex gap-1 mt-4 border-b border-slate-100">
          {[
            { id: 'plans', label: 'Plans', icon: Package },
            { id: 'features', label: 'Feature Catalog', icon: Zap },
            { id: 'addons', label: 'Add-ons', icon: Plus }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-[13px] font-medium flex items-center gap-2 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'text-rose-600 border-rose-600'
                  : 'text-slate-500 border-transparent hover:text-slate-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        
        {/* PLANS TAB */}
        {activeTab === 'plans' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[16px] font-semibold text-slate-800">
                Plans for {selectedIndustry?.name}
              </h2>
              <button
                onClick={() => openPlanModal()}
                className="px-4 py-2 bg-rose-600 text-white rounded-lg text-[13px] font-medium hover:bg-rose-700 transition-colors flex items-center gap-2"
                data-testid="create-plan-btn"
              >
                <Plus className="w-4 h-4" />
                Create New Plan
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {plans.map(plan => (
                <div 
                  key={plan.id} 
                  className={`bg-white rounded-xl border shadow-sm overflow-hidden ${
                    plan.is_active ? 'border-slate-200' : 'border-amber-200 bg-amber-50/30'
                  }`}
                  data-testid={`plan-card-${plan.code}`}
                >
                  <div className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-[15px] font-semibold text-slate-800">{plan.name}</h3>
                        <p className="text-[11px] text-slate-400 font-mono">{plan.code}</p>
                      </div>
                      {!plan.is_active && (
                        <span className="px-2 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-700 rounded">
                          INACTIVE
                        </span>
                      )}
                    </div>
                    
                    <p className="text-[12px] text-slate-500 mb-4 line-clamp-2">
                      {plan.description || 'No description'}
                    </p>
                    
                    <div className="flex items-baseline gap-1 mb-4">
                      <span className="text-[24px] font-bold text-slate-800">₹{plan.price_monthly}</span>
                      <span className="text-[12px] text-slate-400">/month</span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      <div className="p-2 bg-slate-50 rounded-lg">
                        <p className="text-[10px] text-slate-400 uppercase">Staff</p>
                        <p className="text-[14px] font-semibold text-slate-700">{plan.included_staff}</p>
                      </div>
                      <div className="p-2 bg-slate-50 rounded-lg">
                        <p className="text-[10px] text-slate-400 uppercase">Tracking</p>
                        <p className="text-[14px] font-semibold text-slate-700">{plan.included_tracking_staff}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 text-[11px] text-slate-500 mb-4">
                      {plan.field_tracking_enabled ? (
                        <span className="flex items-center gap-1 text-emerald-600">
                          <Check className="w-3 h-3" /> Field Tracking
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-slate-400">
                          <X className="w-3 h-3" /> No Field Tracking
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                      <span className="text-[11px] text-slate-400">
                        {plan.active_subscriptions || 0} subscribers
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => openPlanModal(plan)}
                          className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Edit Plan Info"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openEntitlementsModal(plan)}
                          className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                          title="Edit Entitlements (Modules & Limits)"
                        >
                          <Shield className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deletePlan(plan)}
                          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Delete Plan"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* FEATURES TAB */}
        {activeTab === 'features' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-[16px] font-semibold text-slate-800">Feature Catalog</h2>
                <p className="text-[12px] text-slate-500">Define features that plans can enable/disable or limit</p>
              </div>
              <button
                onClick={() => openFeatureModal()}
                className="px-4 py-2 bg-rose-600 text-white rounded-lg text-[13px] font-medium hover:bg-rose-700 transition-colors flex items-center gap-2"
                data-testid="add-feature-btn"
              >
                <Plus className="w-4 h-4" />
                Add Feature
              </button>
            </div>
            
            {/* Feature type legend */}
            <div className="flex items-center gap-4 p-3 bg-white rounded-lg border border-slate-200 mb-4">
              <span className="text-[11px] text-slate-500 font-medium">Feature Types:</span>
              <span className="flex items-center gap-1 text-[11px] text-blue-600">
                <ToggleLeft className="w-3.5 h-3.5" /> Toggle (on/off)
              </span>
              <span className="flex items-center gap-1 text-[11px] text-amber-600">
                <Hash className="w-3.5 h-3.5" /> Limit (numeric)
              </span>
              <span className="flex items-center gap-1 text-[11px] text-purple-600">
                <GitBranch className="w-3.5 h-3.5" /> Workflow (rule-based)
              </span>
            </div>
            
            {/* Features by category */}
            {Object.entries(featuresByCategory).map(([category, catFeatures]) => (
              <div key={category} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                  <h3 className="text-[13px] font-semibold text-slate-700 capitalize">{category}</h3>
                </div>
                <div className="divide-y divide-slate-100">
                  {catFeatures.map(feature => (
                    <div key={feature.id} className="px-4 py-3 flex items-center justify-between hover:bg-slate-50">
                      <div className="flex items-center gap-3">
                        {getFeatureTypeIcon(feature.is_limitable ? 'limit' : 'toggle')}
                        <div>
                          <p className="text-[13px] font-medium text-slate-700">{feature.name}</p>
                          <p className="text-[11px] text-slate-400 font-mono">{feature.key}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {feature.is_limitable && feature.default_limit_value && (
                          <span className="px-2 py-0.5 bg-amber-50 text-amber-700 text-[11px] font-medium rounded">
                            Default: {feature.default_limit_value}
                          </span>
                        )}
                        {!feature.is_limitable && (
                          <span className={`px-2 py-0.5 text-[11px] font-medium rounded ${
                            feature.default_enabled
                              ? 'bg-emerald-50 text-emerald-700'
                              : 'bg-slate-100 text-slate-500'
                          }`}>
                            {feature.default_enabled ? 'ON by default' : 'OFF by default'}
                          </span>
                        )}
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => openFeatureModal(feature)}
                            className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteFeature(feature)}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            
            {features.length === 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
                <Zap className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-[14px] text-slate-500">No features defined yet</p>
                <p className="text-[12px] text-slate-400 mt-1">Add features to control what plans can offer</p>
              </div>
            )}
          </div>
        )}
        
        {/* ADDONS TAB */}
        {activeTab === 'addons' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-[16px] font-semibold text-slate-800">Add-ons Catalog</h2>
                <p className="text-[12px] text-slate-500">Define purchasable add-ons that extend plan limits</p>
              </div>
              <button
                onClick={() => openAddonModal()}
                className="px-4 py-2 bg-rose-600 text-white rounded-lg text-[13px] font-medium hover:bg-rose-700 transition-colors flex items-center gap-2"
                data-testid="add-addon-btn"
              >
                <Plus className="w-4 h-4" />
                Add Add-on
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {addons.map(addon => (
                <div 
                  key={addon.id} 
                  className={`bg-white rounded-xl border shadow-sm p-5 ${
                    addon.is_active ? 'border-slate-200' : 'border-amber-200 bg-amber-50/30'
                  }`}
                  data-testid={`addon-card-${addon.key}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-[14px] font-semibold text-slate-800">{addon.name}</h3>
                      <p className="text-[11px] text-slate-400 font-mono">{addon.key}</p>
                    </div>
                    {!addon.is_active && (
                      <span className="px-2 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-700 rounded">
                        INACTIVE
                      </span>
                    )}
                  </div>
                  
                  <p className="text-[12px] text-slate-500 mb-3 line-clamp-2">
                    {addon.description || 'No description'}
                  </p>
                  
                  <div className="flex items-center gap-4 mb-3">
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase">Monthly</p>
                      <p className="text-[14px] font-semibold text-slate-700">₹{addon.price_monthly}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase">Yearly</p>
                      <p className="text-[14px] font-semibold text-slate-700">₹{addon.price_yearly}</p>
                    </div>
                  </div>
                  
                  <div className="p-2 bg-blue-50 rounded-lg mb-3">
                    <p className="text-[11px] text-blue-600">
                      <span className="font-medium">+{addon.limit_increase_value}</span> {addon.limit_type?.replace(/_/g, ' ')}
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-end gap-1 pt-3 border-t border-slate-100">
                    <button
                      onClick={() => openAddonModal(addon)}
                      className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteAddon(addon)}
                      className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
            {addons.length === 0 && (
              <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
                <Plus className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-[14px] text-slate-500">No add-ons defined yet</p>
                <p className="text-[12px] text-slate-400 mt-1">Add add-ons to let businesses extend their limits</p>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* PLAN MODAL */}
      {showPlanModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4" data-testid="plan-modal">
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="text-[16px] font-semibold text-slate-800">
                {editingItem ? 'Edit Plan' : 'Create New Plan'}
              </h2>
            </div>
            <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
              <div>
                <label className="block text-[12px] font-medium text-slate-600 mb-1">Plan Name *</label>
                <input
                  type="text"
                  value={planForm.name}
                  onChange={(e) => setPlanForm({...planForm, name: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"
                  placeholder="e.g., Pro Plan"
                />
              </div>
              <div>
                <label className="block text-[12px] font-medium text-slate-600 mb-1">Code</label>
                <input
                  type="text"
                  value={planForm.code}
                  onChange={(e) => setPlanForm({...planForm, code: e.target.value.toUpperCase()})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] font-mono"
                  placeholder="e.g., PRO"
                />
              </div>
              <div>
                <label className="block text-[12px] font-medium text-slate-600 mb-1">Description</label>
                <textarea
                  value={planForm.description}
                  onChange={(e) => setPlanForm({...planForm, description: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"
                  rows={2}
                  placeholder="Describe this plan..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[12px] font-medium text-slate-600 mb-1">Price Monthly (₹)</label>
                  <input
                    type="number"
                    value={planForm.price_monthly}
                    onChange={(e) => setPlanForm({...planForm, price_monthly: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"
                  />
                </div>
                <div>
                  <label className="block text-[12px] font-medium text-slate-600 mb-1">Price Yearly (₹)</label>
                  <input
                    type="number"
                    value={planForm.price_yearly}
                    onChange={(e) => setPlanForm({...planForm, price_yearly: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[12px] font-medium text-slate-600 mb-1">Included Staff</label>
                  <input
                    type="number"
                    value={planForm.included_staff}
                    onChange={(e) => setPlanForm({...planForm, included_staff: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"
                  />
                </div>
                <div>
                  <label className="block text-[12px] font-medium text-slate-600 mb-1">Tracking Staff</label>
                  <input
                    type="number"
                    value={planForm.included_tracking_staff}
                    onChange={(e) => setPlanForm({...planForm, included_tracking_staff: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="field_tracking"
                  checked={planForm.field_tracking_enabled}
                  onChange={(e) => setPlanForm({...planForm, field_tracking_enabled: e.target.checked})}
                  className="rounded border-slate-300"
                />
                <label htmlFor="field_tracking" className="text-[13px] text-slate-600">
                  Enable Field Tracking Module
                </label>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => setShowPlanModal(false)}
                className="px-4 py-2 text-[13px] font-medium text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={savePlan}
                disabled={saving}
                className="px-4 py-2 bg-rose-600 text-white rounded-lg text-[13px] font-medium hover:bg-rose-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingItem ? 'Update Plan' : 'Create Plan'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* FEATURE MODAL */}
      {showFeatureModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4" data-testid="feature-modal">
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="text-[16px] font-semibold text-slate-800">
                {editingItem ? 'Edit Feature' : 'Add New Feature'}
              </h2>
            </div>
            <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
              <div>
                <label className="block text-[12px] font-medium text-slate-600 mb-1">Feature Key *</label>
                <input
                  type="text"
                  value={featureForm.key}
                  onChange={(e) => setFeatureForm({...featureForm, key: e.target.value.toLowerCase().replace(/\s/g, '.')})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] font-mono"
                  placeholder="e.g., catalogue.products.max_count"
                />
                <p className="text-[10px] text-slate-400 mt-1">Use dot notation for hierarchy</p>
              </div>
              <div>
                <label className="block text-[12px] font-medium text-slate-600 mb-1">Feature Name *</label>
                <input
                  type="text"
                  value={featureForm.name}
                  onChange={(e) => setFeatureForm({...featureForm, name: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"
                  placeholder="e.g., Max Products"
                />
              </div>
              <div>
                <label className="block text-[12px] font-medium text-slate-600 mb-1">Category</label>
                <select
                  value={featureForm.category}
                  onChange={(e) => setFeatureForm({...featureForm, category: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"
                >
                  <option value="CATALOGUE">Catalogue</option>
                  <option value="SALES">Sales</option>
                  <option value="PURCHASES">Purchases</option>
                  <option value="POS">POS</option>
                  <option value="FIELD_TRACKING">Field Tracking</option>
                  <option value="FINANCE">Finance</option>
                  <option value="TEAM_OPS">Team & Operations</option>
                  <option value="REPORTS">Reports</option>
                  <option value="INTELLIGENCE">Intelligence</option>
                  <option value="INTEGRATIONS">Integrations</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_limitable"
                  checked={featureForm.is_limitable}
                  onChange={(e) => setFeatureForm({...featureForm, is_limitable: e.target.checked})}
                  className="rounded border-slate-300"
                />
                <label htmlFor="is_limitable" className="text-[13px] text-slate-600">
                  This feature has a numeric limit
                </label>
              </div>
              {featureForm.is_limitable && (
                <div>
                  <label className="block text-[12px] font-medium text-slate-600 mb-1">Default Limit Value</label>
                  <input
                    type="number"
                    value={featureForm.default_limit_value || ''}
                    onChange={(e) => setFeatureForm({...featureForm, default_limit_value: parseInt(e.target.value) || null})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"
                    placeholder="e.g., 500"
                  />
                </div>
              )}
              {!featureForm.is_limitable && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="default_enabled"
                    checked={featureForm.default_enabled}
                    onChange={(e) => setFeatureForm({...featureForm, default_enabled: e.target.checked})}
                    className="rounded border-slate-300"
                  />
                  <label htmlFor="default_enabled" className="text-[13px] text-slate-600">
                    Enabled by default
                  </label>
                </div>
              )}
              <div>
                <label className="block text-[12px] font-medium text-slate-600 mb-1">Description</label>
                <textarea
                  value={featureForm.description}
                  onChange={(e) => setFeatureForm({...featureForm, description: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"
                  rows={2}
                  placeholder="Describe this feature..."
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => setShowFeatureModal(false)}
                className="px-4 py-2 text-[13px] font-medium text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={saveFeature}
                disabled={saving}
                className="px-4 py-2 bg-rose-600 text-white rounded-lg text-[13px] font-medium hover:bg-rose-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingItem ? 'Update Feature' : 'Add Feature'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* ADDON MODAL */}
      {showAddonModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4" data-testid="addon-modal">
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="text-[16px] font-semibold text-slate-800">
                {editingItem ? 'Edit Add-on' : 'Add New Add-on'}
              </h2>
            </div>
            <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
              <div>
                <label className="block text-[12px] font-medium text-slate-600 mb-1">Add-on Key *</label>
                <input
                  type="text"
                  value={addonForm.key}
                  onChange={(e) => setAddonForm({...addonForm, key: e.target.value.toLowerCase().replace(/\s/g, '_')})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px] font-mono"
                  placeholder="e.g., extra_staff_5"
                />
              </div>
              <div>
                <label className="block text-[12px] font-medium text-slate-600 mb-1">Add-on Name *</label>
                <input
                  type="text"
                  value={addonForm.name}
                  onChange={(e) => setAddonForm({...addonForm, name: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"
                  placeholder="e.g., Extra 5 Staff Seats"
                />
              </div>
              <div>
                <label className="block text-[12px] font-medium text-slate-600 mb-1">Description</label>
                <textarea
                  value={addonForm.description}
                  onChange={(e) => setAddonForm({...addonForm, description: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"
                  rows={2}
                  placeholder="Describe this add-on..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[12px] font-medium text-slate-600 mb-1">Price Monthly (₹)</label>
                  <input
                    type="number"
                    value={addonForm.price_monthly}
                    onChange={(e) => setAddonForm({...addonForm, price_monthly: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"
                  />
                </div>
                <div>
                  <label className="block text-[12px] font-medium text-slate-600 mb-1">Price Yearly (₹)</label>
                  <input
                    type="number"
                    value={addonForm.price_yearly}
                    onChange={(e) => setAddonForm({...addonForm, price_yearly: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[12px] font-medium text-slate-600 mb-1">Limit Type</label>
                  <select
                    value={addonForm.limit_type}
                    onChange={(e) => setAddonForm({...addonForm, limit_type: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"
                  >
                    <option value="staff_seats">Staff Seats</option>
                    <option value="tracking_seats">Tracking Staff Seats</option>
                    <option value="products">Products</option>
                    <option value="customers">Customers</option>
                    <option value="invoices_per_month">Invoices/Month</option>
                    <option value="exports_per_month">Exports/Month</option>
                    <option value="templates">Templates</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[12px] font-medium text-slate-600 mb-1">Increase Value</label>
                  <input
                    type="number"
                    value={addonForm.limit_increase_value}
                    onChange={(e) => setAddonForm({...addonForm, limit_increase_value: parseInt(e.target.value) || 1})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"
                    min={1}
                  />
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => setShowAddonModal(false)}
                className="px-4 py-2 text-[13px] font-medium text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={saveAddon}
                disabled={saving}
                className="px-4 py-2 bg-rose-600 text-white rounded-lg text-[13px] font-medium hover:bg-rose-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingItem ? 'Update Add-on' : 'Add Add-on'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* CONFIRM MODAL */}
      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        icon={confirmModal?.icon}
        iconBgClass={confirmModal?.iconBgClass}
        iconClass={confirmModal?.iconClass}
        onConfirm={confirmModal?.onConfirm}
        onClose={() => setConfirmModal(null)}
      />

      {/* ENTITLEMENTS EDITOR MODAL */}
      {showEntitlementsModal && editingPlanForEntitlements && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4" data-testid="entitlements-modal">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="text-[16px] font-semibold text-slate-800">
                  Edit Entitlements — {editingPlanForEntitlements.name}
                </h2>
                <p className="text-[12px] text-slate-500 mt-0.5">Changes will be applied to all businesses on this plan</p>
              </div>
              <button onClick={() => setShowEntitlementsModal(false)} className="p-1.5 text-slate-400 hover:text-slate-600 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-6 max-h-[65vh] overflow-y-auto">
              {/* Module Toggles */}
              <div>
                <h3 className="text-[13px] font-semibold text-slate-700 mb-3 uppercase tracking-wide">Modules</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {[
                    { key: 'home', label: 'Home / Dashboard' },
                    { key: 'catalogue', label: 'Catalogue' },
                    { key: 'sales', label: 'Sales' },
                    { key: 'purchases', label: 'Purchases' },
                    { key: 'finance_billing', label: 'Finance & Billing' },
                    { key: 'team_operations', label: 'Team Operations' },
                    { key: 'attendance', label: 'Attendance' },
                    { key: 'field_tracking', label: 'Field Tracking' },
                    { key: 'intelligence', label: 'Intelligence' },
                    { key: 'settings', label: 'Settings' },
                  ].map(mod => {
                    const enabled = entitlementsForm.modules[mod.key] !== false;
                    return (
                      <button
                        key={mod.key}
                        onClick={() => setEntitlementsForm(prev => ({
                          ...prev,
                          modules: { ...prev.modules, [mod.key]: !enabled }
                        }))}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-[12px] font-medium transition-colors ${
                          enabled
                            ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                            : 'bg-slate-50 border-slate-200 text-slate-400'
                        }`}
                      >
                        {enabled ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                        {mod.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Limits */}
              <div>
                <h3 className="text-[13px] font-semibold text-slate-700 mb-3 uppercase tracking-wide">Limits</h3>
                <p className="text-[11px] text-slate-400 mb-3">Leave blank for unlimited</p>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { key: 'max_staff_total', label: 'Max Staff (Total)' },
                    { key: 'max_tracking_staff', label: 'Max Tracking Staff' },
                    { key: 'max_customers_total', label: 'Max Customers' },
                    { key: 'max_products_total', label: 'Max Products' },
                    { key: 'max_sales_invoices_per_cycle', label: 'Max Sales Invoices / Cycle' },
                    { key: 'max_purchase_invoices_per_cycle', label: 'Max Purchase Invoices / Cycle' },
                  ].map(limit => (
                    <div key={limit.key}>
                      <label className="block text-[11px] font-medium text-slate-500 mb-1">{limit.label}</label>
                      <input
                        type="number"
                        value={entitlementsForm.limits[limit.key] ?? ''}
                        onChange={(e) => setEntitlementsForm(prev => ({
                          ...prev,
                          limits: { ...prev.limits, [limit.key]: e.target.value }
                        }))}
                        placeholder="Unlimited"
                        min={0}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-[13px]"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={() => setShowEntitlementsModal(false)}
                className="px-4 py-2 text-[13px] font-medium text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={saveEntitlements}
                disabled={applyingChanges}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-[13px] font-medium hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
              >
                {applyingChanges && <Loader2 className="w-4 h-4 animate-spin" />}
                Save & Apply to All Businesses
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlansManagementV2;
